import { defineComponent as _defineComponent } from 'vue'
import {computed, onMounted, ref, watch} from 'vue'
import {useRoute, useRouter} from "vue-router/composables";
import {Game, getGames} from "@/api/game";
import store from '@/store'
import HomePage from "@/views/HomePage.vue"
import { startVersionCheck } from '@/utils/versionCheck'
import { getBookDataAccess } from '@/api/bookData'


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const route = useRoute()
const isHomePage = computed(() => route.path === '/' && !isLoggedIn.value)
const isStaticPage = computed(() => ['/privacy', '/login', '/user-agreement', '/minor-protection'].includes(route.path))

// 如果 localStorage 中有 user，则恢复
const savedUser = localStorage.getItem('USER')
if (savedUser) {
  store.commit('user/SET_USER', JSON.parse(savedUser))
}

const router = useRouter()
// 登录态以 Vuex 用户信息为准（响应式）：登录/登出 commit user 后顶栏会自动更新。
// 不能用 localStorage.getItem('TOKEN') —— 它不是响应式数据源，SPA 内跳转不会触发重算，
// 会导致登录后顶栏仍显示「登录」，必须整页刷新才更新。
const isLoggedIn = computed(() => !!store.state.user.user)
console.log(`token vue: ${localStorage.getItem('TOKEN')}`)

const isAdmin = computed(() => {
  return store.state.user.user?.role === 'admin'
})

const isAdminOrEditor = computed(() => {
  const role = store.state.user.user?.role
  return role === 'admin' || role === 'editor'
})

// 【AI 剧情生成】比其他后台工具多开一档给作者：作者要自己写章节，
// 生成器就是他们的主要生产工具。能选到哪些书由列表接口按归属过滤，
// 提示词仍只有 admin 能看和改（后端 config 接口按角色裁字段）。
const canUseAiNodeGenerator = computed(() => {
  const role = store.state.user.user?.role
  return role === 'admin' || role === 'editor' || role === 'author'
})

// 【分书数据】对管理员 + 白名单开放，白名单不限角色、只有后端知道：非管理员登录后问一次后端，
// 换账号（user id 变化）时重新问。请求回来前若已换了账号，丢掉这次结果。
const bookDataAccess = ref(false)
const canSeeBookData = computed(() => isAdmin.value || bookDataAccess.value)
watch(() => store.state.user.user?.id, async (uid) => {
  bookDataAccess.value = false
  if (!uid || isAdmin.value) return
  const allowed = await getBookDataAccess()
  if (store.state.user.user?.id === uid) bookDataAccess.value = allowed
}, { immediate: true })

const games = ref<Game[]>([])
const selectedGame = ref<Game | null>(null)

// 当前用户名
const currentUsername = computed(() => {
  return store.state.user.user?.username || '用户'
})


const logout = () => {
  localStorage.removeItem('TOKEN')
  localStorage.removeItem('USER')
  // 清空 Vuex 用户信息，使 isLoggedIn 立即变为未登录（顶栏无需刷新即可回到「登录」态）
  store.commit('user/SET_USER', null)
  router.push('/login')
}

const handleWorkSelect = async (game: Game) => {
  await selectGame(game)
  // MyWorks.vue 已经自己做了 router.push，这里不再重复导航
}

const refreshGames = async () => {
  games.value = await getGames()
}

watch(() => store.state.user.user, (newVal) => {
  if (newVal) {
    localStorage.setItem('USER', JSON.stringify(newVal))
  } else {
    localStorage.removeItem('USER')
  }
}, { deep: true })

// 监听 selectedGame 的变化，自动更新导航链接
watch(() => selectedGame.value?.nodeVersion, (newVersion, oldVersion) => {
  if (newVersion !== oldVersion) {
    console.log(`游戏节点版本变化: ${oldVersion} -> ${newVersion}`)
    // 如果当前在 node 编辑页面，自动跳转到对应版本的页面
    const currentPath = route.path
    if (currentPath === '/nodeDetail' || currentPath === '/nodeDetailV2') {
      const targetRoute = newVersion === 'v2.0' ? '/nodeDetailV2' : '/nodeDetail'
      if (currentPath !== targetRoute) {
        console.log(`版本变化触发跳转: ${currentPath} -> ${targetRoute}`)
        router.push(targetRoute)
      }
    }
  }
})

const selectGame = async (game: Game) => {
  if (game?.id){
   localStorage.setItem("GameId", game.id.toString())

   // 检测该game的节点版本：先查章节列表，再只加载第一章的节点
   try {
     const [{ getNodes }, { getChapters }] = await Promise.all([
       import('@/api/node'),
       import('@/api/node')
     ])
     const chaptersRes = await getChapters(game.id)
     const chapters = chaptersRes.data?.data || chaptersRes.data || []
     const firstChapterId = chapters.length > 0 ? chapters[0].id : undefined
     const result = await getNodes(game.id, firstChapterId)
     if (result.data && result.data.length > 0) {
       game.nodeVersion = result.data[0].version || '1.0.0'
     }
   } catch (error) {
     console.error('获取节点版本失败:', error)
   }
  }
  // 在版本检测完成后再设置selectedGame,确保nodeVersion已被设置
  selectedGame.value = game

  // 如果当前在编辑页面，自动跳转到对应版本
  const currentPath = route.path
  if (currentPath.includes('/games/') && currentPath.includes('/editor')) {
    // 统一编辑器：仅在游戏 ID 变化时跳转
    if (!currentPath.includes(`/games/${game.id}/`)) {
      router.push(`/games/${game.id}/editor`)
    }
  } else if (currentPath === '/nodeDetail' || currentPath === '/nodeDetailV2') {
    const targetRoute = game.nodeVersion === 'v2.0' ? '/nodeDetailV2' : '/nodeDetail'
    if (currentPath !== targetRoute) {
      router.push(targetRoute)
    }
  }
}

onMounted(() => {
  if (isStaticPage.value) return
  if (!isLoggedIn.value) return

  // 发版后提醒还开着旧页面的同事刷新。放在登录态判断之后：
  // 停在登录页/静态页的人不需要被打扰，会被旧版本坑到的是正在干活的人。
  startVersionCheck()

  // getGames() 与首屏路由解析并发发起，互不等待，避免人为串行拖慢首屏。
  const gamesPromise = getGames()
  // 真正的错误处理在下面 onReady 回调的 catch 里；这里只是防止在
  // 回调执行前（懒加载路由 chunk 还没下载完）该 promise 提前 reject
  // 时被判定成未处理的 rejection。
  gamesPromise.catch(() => { /* 见下方统一处理 */ })

  // 首屏路由跳转是异步的：编辑器等路由的组件是懒加载 chunk（() => import(...)），
  // vue-router 3 要等 resolveAsyncComponents 拿到 chunk、confirmTransition 完成后
  // 才会更新 route.path，在此之前它一直停在初始值 '/'。若在这里直接读 route.path
  // 判断跳转，一旦 getGames() 比 chunk 先返回（本机预热后 getGames 只要几十毫秒，
  // 这种情况很常见），就会把直接用编辑器 URL 打开/刷新/书签进入的作者误判成
  // "停在首页"而弹回「我的作品」。router.onReady 保证回调在首屏导航真正落定
  // （含懒加载 chunk resolve）之后才执行，这里读 route.path 才是安全的。
  router.onReady(async () => {
    try {
      games.value = await gamesPromise

      // If logged in and at root, redirect to my-works
      if (route.path === '/') {
        router.replace('/my-works')
      }

      // If URL contains a game ID, auto-enter that work
      const urlMatch = route.path.match(/\/games\/(\d+)\//)
      if (urlMatch) {
        const gameId = urlMatch[1]
        const targetGame = games.value.find(g => g.id.toString() === gameId)
        if (targetGame) {
          await selectGame(targetGame)
          return
        }
      }

      // 不再自动选中游戏，必须通过"我的作品"点击进入
    } catch (e) {
      console.error('获取游戏列表失败', e)
    }
  })
})


return { __sfc: true,route, isHomePage, isStaticPage, savedUser, router, isLoggedIn, isAdmin, isAdminOrEditor, canUseAiNodeGenerator, bookDataAccess, canSeeBookData, games, selectedGame, currentUsername, logout, handleWorkSelect, refreshGames, selectGame, HomePage }
}

})